<script setup lang="ts">
import {
  CreateImageStepUpload,
  CreateImageStepUploadMutation,
  CreateImageStepUploadMutationVariables,
  SaveImageStepUploadEditMutation,
  SaveImageStepUploadEditMutationVariables,
  UpdateImageStepUpload,
} from '@/generated/graphql'
import { computed, reactive, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { PickedUpload } from '@/components/upload/UploadPicker.vue'
import useSave from '@/composables/useSave'
import FormImageStepUpload from '@/components/stepUpload/FormImageStepUpload.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import { useI18n } from 'vue-i18n'
import { DraftImageEdit } from '@/utils/imageEdit'

const props = defineProps<{
  stepId: string
  upload: PickedUpload
}>()
const emit = defineEmits<{
  created: []
}>()

const { t } = useI18n()

type Model = Partial<CreateImageStepUpload>
const model = reactive<Model>({})
const mutation = useMutation<CreateImageStepUploadMutation, CreateImageStepUploadMutationVariables>(
  gql`
    mutation CreateImageStepUpload($command: CreateImageStepUpload!) {
      product {
        createImageStepUpload(command: $command) {
          id
        }
      }
    }
  `,
)
const saveEditMutation = useMutation<
  SaveImageStepUploadEditMutation,
  SaveImageStepUploadEditMutationVariables
>(gql`
  mutation SaveImageStepUploadEdit($command: SaveImageStepUploadEdit!) {
    product {
      saveImageStepUploadEdit(command: $command) {
        id
      }
    }
  }
`)
const mutate = async (edit?: DraftImageEdit) => {
  model.stepId = props.stepId
  model.uploadId = props.upload.id
  const result = await mutation.mutate({ command: model as CreateImageStepUpload })
  const createdId = result?.data?.product.createImageStepUpload.id
  try {
    if (createdId && edit) {
      await saveEditMutation.mutate({ command: { id: createdId, ...edit } })
    }
  } finally {
    emit('created')
  }
}

const form = ref<ComponentExposed<typeof FormImageStepUpload>>()
const save = computed(() => useSave(() => mutate(), form.value?.validation))
</script>

<template>
  <form-image-step-upload
    ref="form"
    v-model="model as CreateImageStepUpload & UpdateImageStepUpload"
    :upload-id="props.upload.id"
    :add-draft="mutate"
  />
  <v-btn color="primary" class="float-right" v-bind="save.saveButtonProps.value">
    {{ t('button.add') }}
  </v-btn>
</template>

<style scoped lang="scss"></style>
